import type { UnitType } from "../../../lib/api";

/**
 * Presentational view-model for booking-promotion admin UI.
 *
 * Field names mirror the frozen Gate0 contract exactly (see
 * `.slim/deepwork/booking-discounts.md`, "Frozen implementation contract"):
 * a future API binding can adapt these types mechanically without redesign.
 * Nothing here fetches, stores, or mutates data — all actions arrive as
 * callback props from the (Phase 2) container.
 */

export type OfferMode = "auto" | "code";
export type DiscountType = "percent" | "fixed";

/** Exact PromotionWrite shape from the frozen contract. */
export interface PromotionConfiguration {
  name_en: string;
  name_ar: string;
  offer_mode: OfferMode;
  /** Uppercase code for code offers, null for automatic offers. */
  code: string | null;
  discount_type: DiscountType;
  /** Decimal string, set only for percent offers. */
  discount_percent: string | null;
  /** Decimal money string, set only for fixed offers. */
  fixed_amount: string | null;
  /** Optional per-booking savings cap, decimal money string. */
  max_discount_amount: string | null;
  /** ISO8601 UTC, start-inclusive. */
  starts_at: string;
  /** ISO8601 UTC, end-exclusive, null for no end. */
  ends_at: string | null;
  /** Empty means every venue. */
  venue_ids: string[];
  /** Empty means every unit type. */
  unit_types: UnitType[];
  /** Weekdays use the JavaScript convention: 0 = Sunday … 6 = Saturday (not the Python Monday-0 convention).
   *  Matched against the booked START in venue timezone. Empty means every day. */
  weekdays: number[];
  /** HH:mm venue-local window, matched against the booked START. Null means no bound. */
  local_start_time: string | null;
  /** HH:mm venue-local window, matched against the booked START. Null means no bound. */
  local_end_time: string | null;
  first_booking_only: boolean;
  /** Campaign budget, decimal money string. Null means no budget limit. */
  budget_amount: string | null;
  /** Total uses across ALL revisions. Null means unlimited. */
  max_uses: number | null;
  /** Uses per customer across ALL revisions. Null means unlimited. */
  per_customer_max_uses: number | null;
}

/**
 * Real supplied metrics. Money is a decimal string shown as-is (EGP);
 * null means the value is unknown (legacy history) — render "Unknown",
 * never a measured 0, and never convert or recompute anything.
 * remaining_* is null for unlimited — render "No limit", never 0.
 */
export interface PromotionMetrics {
  reserved_uses: number;
  consumed_uses: number;
  released_uses: number;
  reserved_credit_amount: string | null;
  consumed_credit_amount: string | null;
  released_credit_amount: string | null;
  refunded_credit_amount: string | null;
  remaining_budget_amount: string | null;
  remaining_uses: number | null;
}

/** Stable record envelope from the frozen contract. */
export interface PromotionRecord {
  id: string;
  /** Normalized code, null for automatic offers. */
  code: string | null;
  /** Controls NEW reservations only; accepted holds are never evicted. */
  is_active: boolean;
  current_revision: {
    id: string;
    revision_number: number;
    configuration: PromotionConfiguration;
  };
  metrics: PromotionMetrics;
}

/** One drilldown row for the redemption report. IDs only — no customer contact details. */
export interface PromotionRedemptionRow {
  booking_id: string;
  status: "reserved" | "released" | "consumed";
  reserved_credit_amount: string | null;
  /**
   * Actual server fields (promotion_schemas.py authoritative). Display prefers
   * the final credit once known, the hold time as the booking time, and the
   * closing instant matching the status. Gross commission is private admin
   * reporting (never customer-facing): the reserved estimate until consumed,
   * then the finalized amount — a funding basis, never current earnings.
   */
  final_credit_amount: string | null;
  reserved_at: string | null;
  consumed_at: string | null;
  released_at: string | null;
  venue_id?: string | null;
  revision_id?: string | null;
  reserved_gross_commission_amount?: string | null;
  final_gross_commission_amount?: string | null;
}

/** External venue choices supplied by the container (never invented here). */
export interface PromotionVenueOption {
  id: string;
  name: string;
}

/** Normalize an entered code the same way the contract expects: trimmed, uppercase. */
export function normalizePromotionCode(value: string): string {
  return value.trim().toUpperCase();
}

/** Blank form state: every scope open, every limit unset. */
export function emptyPromotionConfiguration(): PromotionConfiguration {
  return {
    name_en: "",
    name_ar: "",
    offer_mode: "auto",
    code: null,
    discount_type: "percent",
    discount_percent: null,
    fixed_amount: null,
    max_discount_amount: null,
    starts_at: "",
    ends_at: null,
    venue_ids: [],
    unit_types: [],
    weekdays: [],
    local_start_time: null,
    local_end_time: null,
    first_booking_only: false,
    budget_amount: null,
    max_uses: null,
    per_customer_max_uses: null,
  };
}
